import type { CreatorStatus } from './enums.js';

/**
 * Creator-side constants both the Worker and the screens need at runtime.
 *
 * No zod — see `campaigns.ts` for why the schemas live in `contracts.ts`.
 */

/* ------------------------------------------------------------------ */
/* Invitations                                                         */
/* ------------------------------------------------------------------ */

/** Every invitation code starts with this. */
export const INVITE_CODE_PREFIX = 'nxi-';

/** Characters after the prefix. 160 bits of randomness, base32-ish. */
export const INVITE_CODE_BODY_LENGTH = 32;

/** The same unambiguous alphabet as the bio code: a person reads these aloud. */
export const INVITE_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';

/** How long an invitation stays usable unless the operator says otherwise. */
export const INVITE_DEFAULT_TTL_DAYS = 30;

export function isInviteCode(value: string): boolean {
  const re = new RegExp(
    `^${INVITE_CODE_PREFIX}[${INVITE_CODE_ALPHABET}]{${INVITE_CODE_BODY_LENGTH}}$`,
    'i',
  );
  return re.test(value.trim());
}

/* ------------------------------------------------------------------ */
/* Platform terms                                                      */
/* ------------------------------------------------------------------ */

/**
 * The audience string on `PlatformTermsDocument`. v1 has one audience; the
 * column exists because a client-facing document is the obvious second.
 */
export const CREATOR_TERMS_AUDIENCE = 'CREATOR';

/* ------------------------------------------------------------------ */
/* Profile                                                             */
/* ------------------------------------------------------------------ */

/**
 * Countries are ISO 3166-1 alpha-2, upper-case.
 *
 * A free-text country field produces "UK", "U.K.", "United Kingdom" and
 * "england" for one place, and the payout rail and the tax threshold both key
 * on the country — so this is the one profile field that is a code and not
 * prose. The screen renders the name; the database stores the code.
 */
export function isCountryCode(value: string): boolean {
  return /^[A-Z]{2}$/.test(value);
}

/**
 * The payout profile placeholder.
 *
 * A creator's payout profile exists from signup with `provider: MANUAL` and
 * `status: PENDING`. Phase 5 gave it a destination the creator sets themselves
 * (sealed under `TOKEN_KEK`), but the RAIL is still a person: Stripe Connect is
 * stubbed and `CreatorPayoutProfile` refuses `ELIGIBLE` without a tax-process
 * reference the operator records. So the note says what actually happens on a
 * Monday, rather than implying an automatic transfer that does not exist.
 */
export const PAYOUT_PLACEHOLDER_NOTE =
  'Payouts are sent by hand for the pilot: the operator approves the Monday batch and pays each one, then records the reference. You will see it on your wallet as soon as it has gone.';

/* ------------------------------------------------------------------ */
/* Review decisions                                                    */
/* ------------------------------------------------------------------ */

/**
 * Which creator status may follow which, and nothing else.
 *
 * A creator signs up as `PENDING_REVIEW` and `evaluateEligibility` refuses
 * anybody who is not `APPROVED`, so this map is the only door into a campaign.
 *
 * `REJECTED` is the one dead end, on purpose. A rejection is a decision the
 * creator has already been told about in words; un-rejecting somebody by a
 * status flip would leave that sentence standing on their Profile with no
 * record of what changed, so a reconsideration is a new signup rather than an
 * edit.
 *
 * `INVITED` IS REVIEWABLE, and must be. Nothing writes it today — signup
 * writes `PENDING_REVIEW` — but it is the COLUMN DEFAULT on `CreatorProfile`,
 * so it is what any future insert that forgets the field produces, and the
 * admin screen already counts it as somebody waiting. Given no exits it was a
 * one-way door: a profile in it could never be approved, never be rejected,
 * and would sit in the operator's "waiting for approval" count for ever with
 * no button under it. It gets the same two verdicts as `PENDING_REVIEW`,
 * because a person holding it is in the same position — an account nobody has
 * decided about yet.
 */
export const CREATOR_STATUS_TRANSITIONS: Readonly<
  Record<CreatorStatus, readonly CreatorStatus[]>
> = Object.freeze({
  INVITED: ['APPROVED', 'REJECTED'],
  PENDING_REVIEW: ['APPROVED', 'REJECTED'],
  APPROVED: ['SUSPENDED'],
  SUSPENDED: ['APPROVED'],
  REJECTED: [],
});

/**
 * The statuses that mean "nobody has decided about this person yet".
 *
 * One list, so the admin screen's "Needs review" filter, its count, and the
 * Telegram notifier cannot disagree about who is waiting. They did: the screen
 * counted `INVITED` and the notifier did not, which would have shown the
 * operator a number nothing ever told them about.
 */
export const CREATOR_STATUSES_AWAITING_DECISION = ['PENDING_REVIEW', 'INVITED'] as const;

export function creatorAwaitsDecision(status: CreatorStatus): boolean {
  return (CREATOR_STATUSES_AWAITING_DECISION as readonly CreatorStatus[]).includes(status);
}

/** The three verdicts an operator may record. */
export const CREATOR_DECISION_STATUSES = ['APPROVED', 'REJECTED', 'SUSPENDED'] as const;
export type CreatorDecisionStatus = (typeof CREATOR_DECISION_STATUSES)[number];

/**
 * Which verdicts need a sentence, and which must not carry one.
 *
 * A rejection and a suspension both cost the creator money and both are shown
 * to them word for word, so neither may be recorded without one. An approval
 * is the absence of a problem: a "reason" there would be an operator's private
 * note on a screen the creator reads, which is how a note becomes a grievance.
 */
export function creatorDecisionNeedsReason(status: CreatorDecisionStatus): boolean {
  return status === 'REJECTED' || status === 'SUSPENDED';
}

/* ------------------------------------------------------------------ */
/* The operator's Creators page                                        */
/* ------------------------------------------------------------------ */

/**
 * What the Creators page can show. `NEWEST` is everyone, newest first — the
 * default once auto-approval is on, because there is no queue to lead with.
 * `NEEDS_REVIEW` is still there for the manual mode and for anybody parked in
 * it. `UNCONFIRMED` and `NO_CONNECTION` are the two launch-day questions:
 * who cannot submit yet, and why.
 */
export const ADMIN_CREATOR_FILTERS = [
  'NEWEST',
  'NEEDS_REVIEW',
  'APPROVED',
  'SUSPENDED',
  'REJECTED',
  'UNCONFIRMED',
  'NO_CONNECTION',
] as const;
export type AdminCreatorFilter = (typeof ADMIN_CREATOR_FILTERS)[number];

export const ADMIN_CREATORS_DEFAULT_PAGE_SIZE = 25;
export const ADMIN_CREATORS_MAX_PAGE_SIZE = 100;
/** Search terms are `instr` needles, never LIKE patterns; the cap is manners. */
export const ADMIN_CREATOR_SEARCH_MAX_LENGTH = 80;
/** How many ids one bulk approval may carry. */
export const ADMIN_CREATORS_BULK_MAX = 100;
